import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { caseStudyAPI } from '../../features/case-study/api/casestudyAPI';
import { websiteInterviewAPI } from '../../features/website-interview/api/websiteInterviewAPI';
import './CaseStudy.css';

const SCORE_LABELS = {
  evidence_use: 'Evidence use',
  logic_and_causality: 'Logic and causality',
  trade_off_analysis: 'Trade-off analysis',
  feasibility_and_execution: 'Feasibility and execution',
  risk_awareness: 'Risk awareness',
};

const JARGON_RE =
  /isomorphism|subgraph|source move|node coverage|edge coverage|under-developed|graph updates|edges synced|case graph|%\s*isomorphism|mechanism\s+\w+\s*→/i;

const humanizeConceptLabel = (raw) => {
  if (!raw) return '';
  let s = String(raw).trim();
  s = s.replace(/^(lemma_|RG_|TG_|KG_|LG_)/i, '');
  s = s.replace(/^[KJLN]\d+_/, '');
  s = s.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const activationPlain = (raw) => {
  const s = String(raw || '').toLowerCase();
  if (s === 'spontaneous') return 'used spontaneously';
  if (s === 'strong') return 'strong';
  if (s === 'medium') return 'partly used';
  if (s === 'weak') return 'only hinted';
  if (s === 'absent') return 'missing';
  if (s === 'prompted') return 'prompted';
  return raw || '';
};

const evidenceKind = (row) => {
  const g = String(row?.graph || '').toUpperCase();
  if (g === 'TG' || g === 'RG' || g === 'REASONING') return 'Reasoning';
  if (g === 'LG' || g === 'KG' || g === 'KNOWLEDGE') return 'Knowledge';
  return row?.graph ? String(row.graph) : 'Reasoning';
};

const looksLikeQuestionStem = (text) => /^\s*how does the fact\b/i.test(String(text || ''));

const formatOverallVerdict = (verdict) => {
  if (!verdict) return '';
  if (typeof verdict === 'string') {
    if (JARGON_RE.test(verdict) || looksLikeQuestionStem(verdict)) return '';
    return verdict;
  }
  if (typeof verdict === 'object') {
    const sentence = verdict.sentence || verdict.summary || '';
    if (!sentence || JARGON_RE.test(sentence) || looksLikeQuestionStem(sentence)) return '';
    return sentence.trim();
  }
  return String(verdict);
};

const formatMissedItem = (item) => {
  if (item == null) return '';
  if (typeof item === 'string') {
    return item.replace(/^case graph\s*/i, '').trim();
  }
  if (typeof item === 'object') {
    const concepts = Array.isArray(item.concepts_absent) && item.concepts_absent.length
      ? item.concepts_absent.map(humanizeConceptLabel)
      : [];
    const gap = item.gap || item.miss || '';
    if (concepts.length && gap) return `${concepts.join(', ')} — ${gap}`;
    if (concepts.length) return concepts.join(', ');
    return String(gap).replace(/^case graph\s*/i, '').trim();
  }
  return String(item);
};

const formatHowToFixItem = (item) => {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (typeof item === 'object') {
    const label = item.addresses_gap ? `${item.addresses_gap}: ` : '';
    return `${label}${item.action || ''}`.trim();
  }
  return String(item);
};

const formatCoverage = (raw) => {
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return { total: raw };
  if (typeof raw === 'object') return raw;
  return null;
};

const coverageFromItem = (data, item) => {
  const fromStructured =
    formatCoverage(data?.reasoning_coverage) || formatCoverage(data?.isomorphism_score);
  if (fromStructured && fromStructured.total != null) return fromStructured;
  return formatCoverage(item?.isomorphism_score);
};

const bandLabel = (band) => {
  const s = String(band || '').trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const bandHeadline = (item) => {
  const band = String(item?.isomorphism_score?.band || item?.feedback_structured?.reasoning_coverage?.band || '').toLowerCase();
  if (band === 'strong') return 'Strong — the step is clear and well evidenced.';
  if (band === 'adequate') return 'On the right track — the step is there, but the case evidence is still thin.';
  if (band === 'developing') return 'Developing — you have part of the step, but the link or the case facts are incomplete.';
  return '';
};

const renderStructuredFeedback = (data, item) => {
  if (!data && !item) return null;
  const scores = data?.scores;
  const verdictText = formatOverallVerdict(data?.overall_verdict) || bandHeadline(item);
  const coverage = coverageFromItem(data, item);
  const hasScores = scores && typeof scores === 'object' && Object.keys(scores).length > 0;
  const outline = (Array.isArray(data?.evidence_outline) ? data.evidence_outline : []).filter(
    (row) => row && String(row.evidence_phrase || '').trim()
  );
  const flaws = (Array.isArray(data?.major_flaws) ? data.major_flaws : []).filter(
    (row) => !JARGON_RE.test(typeof row === 'string' ? row : formatMissedItem(row))
  );
  if (!hasScores && !verdictText && !outline.length && !(coverage && coverage.total != null)) return null;

  return (
    <div className="feedback-structured">
      {verdictText ? (
        <div className="feedback-verdict-block">
          <span className="feedback-kicker">Overall verdict</span>
          <p className="feedback-verdict-text">{verdictText}</p>
        </div>
      ) : null}
      {coverage && coverage.total != null ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">Reasoning coverage</h5>
          <p className="feedback-score-line">
            <span className="feedback-score-value">
              {typeof coverage.total === 'number' ? coverage.total.toFixed(2) : coverage.total}
            </span>
            {coverage.band ? (
              <span className="feedback-score-emdash"> — {bandLabel(coverage.band)}</span>
            ) : null}
          </p>
        </div>
      ) : null}
      {outline.length > 0 ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">Evidence from your answer</h5>
          <ul className="feedback-bullet-list">
            {outline.map((row, i) => {
              const label = humanizeConceptLabel(row.label || row.concept || row.primitive);
              return (
                <li key={i}>
                  <strong>
                    {evidenceKind(row)}
                    {label ? ` · ${label}` : ''}
                    {row.activation ? ` (${activationPlain(row.activation)})` : ''}
                  </strong>
                  {': '}
                  “{row.evidence_phrase}”
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      {hasScores ? (
        <div className="feedback-scores-block">
          <span className="feedback-kicker">Scores (0–10)</span>
          {Object.keys(SCORE_LABELS).map((key) => {
            const row = scores[key];
            if (!row) return null;
            const s = typeof row.score === 'number' ? row.score : parseInt(row.score, 10) || 0;
            const detail = row.comment || row.justification || '';
            const cleanDetail = JARGON_RE.test(detail) ? '' : detail;
            return (
              <div key={key} className="feedback-score-row">
                <div className="feedback-score-heading">{SCORE_LABELS[key]}</div>
                <p className="feedback-score-line">
                  <span className="feedback-score-value">{s}/10</span>
                  {cleanDetail ? (
                    <>
                      <span className="feedback-score-emdash"> — </span>
                      <span>{cleanDetail}</span>
                    </>
                  ) : null}
                </p>
              </div>
            );
          })}
        </div>
      ) : null}
      {flaws.length > 0 ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">Major flaws</h5>
          <ul className="feedback-bullet-list">
            {flaws.map((row, i) => (
              <li key={i}>{typeof row === 'string' ? row : formatMissedItem(row)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {Array.isArray(data?.missed_from_case) && data.missed_from_case.length > 0 ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">What you missed from the case</h5>
          <ul className="feedback-bullet-list">
            {data.missed_from_case.map((row, i) => (
              <li key={i}>{formatMissedItem(row)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {Array.isArray(data?.how_to_fix) && data.how_to_fix.length > 0 ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">How to fix your answer</h5>
          <ul className="feedback-bullet-list">
            {data.how_to_fix.map((row, i) => (
              <li key={i}>{formatHowToFixItem(row)}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Oxford tutorial panel — same APIs/graph writes as the former full page.
 * When embedded in CaseStudy, section dots / wheel advance to the next section.
 */
const CaseTutorialPanel = ({
  caseStudyId,
  tutorialPreload,
  onContinueToDiscussion,
  onBackToCase,
}) => {
  const navigate = useNavigate();
  const id = caseStudyId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [attemptId, setAttemptId] = useState(null);
  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState('answering');
  const [feedbackResults, setFeedbackResults] = useState([]);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tutorialEditorRef = useRef(null);
  const pendingExtractRef = useRef(Promise.resolve());

  const current = questions[index] || null;

  const stopMediaTracks = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchTutorial = () =>
      caseStudyAPI.getCaseTutorials(id).then((payload) => {
        const qs = payload.questions || [];
        if (!qs.length) return { payload, attempt: null };
        return caseStudyAPI.startCaseTutorialAttempt(id).then((attempt) => ({ payload, attempt }));
      });
    const boot = async () => {
      try {
        setLoading(true);
        const usePreload = tutorialPreload && tutorialPreload.caseStudyId === id;
        const { payload, attempt } = usePreload ? await tutorialPreload.promise : await fetchTutorial();
        if (cancelled) return;
        const qs = payload.questions || [];
        setQuestions(qs);
        if (!qs.length) {
          setError('No stored tutorial questions for this case.');
          return;
        }
        setAttemptId(attempt.attempt_id);
        pendingExtractRef.current = Promise.resolve();
      } catch (err) {
        if (!cancelled) {
          const msg = err?.data?.error || err?.message || 'Failed to load tutorial';
          if (err?.status === 401) {
            navigate('/login', {
              state: { from: { pathname: `/case-study/${id}`, search: '?focus=tutorial' } },
              replace: true,
            });
            return;
          }
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
      stopMediaTracks();
    };
  }, [id, navigate, tutorialPreload]);

  const startRecording = async () => {
    if (recording || submitting) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.start();
      setRecording(true);
    } catch (err) {
      setError(err?.message || 'Microphone access failed');
    }
  };

  const stopRecordingAndTranscribe = async () => {
    if (!mediaRecorderRef.current || !recording) return;
    setRecording(false);
    setTranscribing(true);
    try {
      const blob = await new Promise((resolve, reject) => {
        const recorder = mediaRecorderRef.current;
        recorder.onstop = () => {
          resolve(new Blob(chunksRef.current, { type: 'audio/webm' }));
        };
        recorder.onerror = reject;
        recorder.stop();
      });
      stopMediaTracks();
      const result = await websiteInterviewAPI.transcribeAudioBlob(blob);
      const text = (result?.text || result?.transcript || '').trim();
      if (text) {
        setAnswer((prev) => {
          const merged = prev ? `${prev.trim()} ${text}` : text;
          if (tutorialEditorRef.current) {
            tutorialEditorRef.current.textContent = merged;
          }
          return merged;
        });
      } else if (result?.error) {
        setError(result.error);
      }
    } catch (err) {
      setError(err?.message || 'Transcription failed');
    } finally {
      setTranscribing(false);
      mediaRecorderRef.current = null;
      chunksRef.current = [];
    }
  };

  const finishTutorial = async () => {
    // Wait for background graph extracts so end feedback has activations.
    try {
      await pendingExtractRef.current;
    } catch {
      /* extract errors are non-fatal; complete retries deferred rows server-side */
    }
    const completed = await caseStudyAPI.completeCaseTutorialAttempt(attemptId);
    setFeedbackResults(completed.feedback || []);
    setPhase('done');
  };

  const handleSubmit = async () => {
    if (!current || !attemptId || submitting) return;
    const trimmed = answer.trim();
    if (trimmed.length < 20) {
      setError('Please give a fuller answer (at least a few sentences).');
      return;
    }
    setError(null);
    setSubmitting(true);
    const isLast = index + 1 >= questions.length;
    try {
      const result = await caseStudyAPI.submitCaseTutorialResponse(attemptId, {
        tutorial_question_id: current.id,
        response_text: trimmed,
      });

      setAnswer('');
      if (tutorialEditorRef.current) {
        tutorialEditorRef.current.innerHTML = '';
      }

      // Graph/LLM scoring runs in background (same as Harvard discussion).
      if (result?.response_id) {
        pendingExtractRef.current = Promise.all([
          pendingExtractRef.current,
          caseStudyAPI.extractCaseTutorialResponse(attemptId, result.response_id).catch(() => null),
        ]);
      }

      const done =
        isLast ||
        result.already_complete ||
        (result.questions_answered != null &&
          result.questions_total != null &&
          result.questions_answered >= result.questions_total);
      if (done) {
        await finishTutorial();
      } else {
        setIndex(index + 1);
      }
    } catch (err) {
      const msg = err?.data?.error || err?.message || 'Failed to submit answer';
      const aborted =
        err?.name === 'AbortError' || /aborted|timeout|signal/i.test(String(msg));
      const already = /already answered|question \d+ before moving/i.test(String(msg));
      if (isLast && (aborted || already || err?.status === 400)) {
        try {
          await finishTutorial();
          return;
        } catch (completeErr) {
          const cmsg = completeErr?.data?.error || completeErr?.message || msg;
          if (/all tutorial questions first/i.test(String(cmsg))) {
            setError('Submit timed out before the answer saved. Please click Submit again.');
          } else {
            setError(cmsg);
          }
          return;
        }
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = () => {
    if (typeof onContinueToDiscussion === 'function') {
      onContinueToDiscussion();
      return;
    }
    navigate(`/case-study/${id}?focus=discussion`);
  };

  const handleBack = () => {
    if (typeof onBackToCase === 'function') {
      onBackToCase();
      return;
    }
    navigate(`/case-study/${id}`);
  };

  if (loading) {
    return (
      <section className="decision-section-animated case-assessment-section natively-scrollable">
        <div className="case-study-loading">Loading Oxford tutorial…</div>
      </section>
    );
  }

  if (error && !questions.length) {
    return (
      <section className="decision-section-animated case-assessment-section natively-scrollable">
        <div className="case-study-error">
          <h2>Could not load tutorial</h2>
          <p>{error}</p>
          <button type="button" className="decision-submit-btn" onClick={handleBack}>
            Back to case
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="decision-section-animated case-assessment-section natively-scrollable" data-tour="case-tutorial">
      <div className="feedback-section natively-scrollable">
        {phase === 'done' ? (
          <>
            <div className="decision-header-block">
              <div className="section-eyebrow">Oxford Tutorial</div>
              <p className="case-assessment-kicker">Oxford tutorial</p>
              <h2 className="decision-main-title">Tutorial complete</h2>
              <div className="section-divider" />
              <p className="decision-main-subtitle">
                Each answer was marked like a tutorial: whether you made the reasoning step,
                and whether you used the case facts that support it.
              </p>
            </div>
            {feedbackResults.map((item) => (
              <div key={item.question_id || item.rank} className="feedback-content" style={{ marginBottom: 28 }}>
                <p style={{ opacity: 0.7, marginBottom: 12 }}>Question {item.rank}</p>
                {item.feedback_structured
                  ? renderStructuredFeedback(item.feedback_structured, item)
                  : (
                    <p>
                      {item.feedback_text && !JARGON_RE.test(item.feedback_text)
                        ? item.feedback_text
                        : bandHeadline(item)}
                    </p>
                  )}
              </div>
            ))}
            <div className="feedback-actions">
              <button type="button" className="decision-submit-btn" onClick={handleContinue}>
                Continue to Harvard Case Method
              </button>
              <button type="button" className="try-again-btn" onClick={handleBack}>
                Back to Writing Task
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="decision-header-block">
              <div className="section-eyebrow">Oxford Tutorial</div>
              <h2 className="decision-main-title">Answer three tutorial questions</h2>
              <div className="section-divider" />
              <p className="case-assessment-kicker case-assessment-kicker--bold">
                Oxford Tutorial · Question {index + 1} of {questions.length}
              </p>
              <p className="case-tutorial-question-text">
                {current?.stem}
                {current?.word_limit_min || current?.word_limit_max ? (
                  <span>
                    {' Aim for about '}
                    {current.word_limit_min || 150}
                    {current.word_limit_max ? `–${current.word_limit_max}` : '+'} words
                    {answer.trim()
                      ? ` · currently ~${answer.trim().split(/\s+/).filter(Boolean).length}`
                      : '.'}
                  </span>
                ) : null}
              </p>
            </div>

            <div className="decision-input-animated">
              <div className="rich-text-editor">
                <div className="rich-text-toolbar" role="toolbar" aria-label="Text formatting">
                  <button
                    type="button"
                    className="rich-text-toolbar-btn"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      document.execCommand('bold', false);
                    }}
                    disabled={submitting || recording || transcribing}
                    aria-label="Bold"
                    title="Bold"
                  >
                    <strong>B</strong>
                  </button>

                  <button
                    type="button"
                    className="rich-text-toolbar-btn"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      document.execCommand('italic', false);
                    }}
                    disabled={submitting || recording || transcribing}
                    aria-label="Italic"
                    title="Italic"
                  >
                    <em>I</em>
                  </button>

                  <button
                    type="button"
                    className="rich-text-toolbar-btn"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      document.execCommand('underline', false);
                    }}
                    disabled={submitting || recording || transcribing}
                    aria-label="Underline"
                    title="Underline"
                  >
                    <u>U</u>
                  </button>
                </div>

                <div
                  ref={tutorialEditorRef}
                  className="rich-text-editor-content"
                  contentEditable={!(submitting || recording || transcribing)}
                  suppressContentEditableWarning
                  data-placeholder="Explain the mechanism — speak or type your answer. Feedback appears after all questions."
                  onInput={(e) => setAnswer(e.currentTarget.innerText || '')}
                  onBlur={(e) => setAnswer(e.currentTarget.innerText || '')}
                />
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 16 }}>
                {!recording ? (
                  <button
                    type="button"
                    className="decision-submit-btn decision-submit-btn--compact"
                    onClick={startRecording}
                    disabled={submitting || transcribing}
                  >
                    {transcribing ? 'Transcribing…' : 'Speak Answer'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="decision-submit-btn decision-submit-btn--compact"
                    onClick={stopRecordingAndTranscribe}
                  >
                    Stop &amp; transcribe
                  </button>
                )}
                <button
                  type="button"
                  className="decision-submit-btn decision-submit-btn--compact"
                  onClick={handleSubmit}
                  disabled={submitting || recording || transcribing || answer.trim().length < 20}
                >
                  {submitting
                    ? (index + 1 < questions.length
                      ? 'Saving your answer…'
                      : 'Preparing your tutorial feedback…')
                    : (index + 1 < questions.length
                      ? 'Submit & Next Question'
                      : 'Submit final answer')}
                </button>
              </div>
            </div>

            {error ? (
              <p style={{ color: 'var(--iv-danger)', marginTop: 16 }}>{error}</p>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
};

export default CaseTutorialPanel;
